import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams, Outlet } from "react-router-dom";
import suburbsData from "../../../data/us_addresses.json";
import SuccessModal from "./SuccessModal";
import { useOutletContext } from "react-router-dom";
import { FaAngleLeft, FaSearch } from "react-icons/fa";
import config from "../../../config/config";
import axios from "axios";

const LocationsComponent = ({ user }) => {
  const [suburbValue, setSuburbValue] = useState("");
  const [primaryLocation, setPrimaryLocation] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [addedLocations, setAddedLocations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const baseURL = config.API_BASE_URL;
  const { isSmallScreen, handleBackClick } = useOutletContext();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      if (user && user.id) {
        try {
          const response = await axios.get(
            `${baseURL}/v1/worker-users/${user.id}`
          );
          const fetchedUserData = response.data;

          if (
            fetchedUserData &&
            fetchedUserData.primaryLocation &&
            fetchedUserData.serviceLocations
          ) {
            setPrimaryLocation(fetchedUserData.primaryLocation);
            setAddedLocations(fetchedUserData.serviceLocations);
          }
        } catch (error) {
          console.error("Failed to fetch user data:", error);
        }
      }
    };

    fetchUserData();
  }, [user, baseURL]);

  useEffect(() => {
    if (inputRef.current && !isSmallScreen) {
      inputRef.current.focus();
    }
  }, [isSmallScreen]);

  const handleSuburbChange = (e) => {
    const inputValue = e.target.value;
    setSuburbValue(inputValue);

    const q = inputValue.trim().toLowerCase();
    const MAX_RESULTS = 5;

    if (!q) {
      setSuggestions([]);
      return;
    }

    const filtered = suburbsData.Suburbs_Zipcodes.filter((addr) => {
      return (
        addr.Post_City.toLowerCase().startsWith(q) ||
        addr.State.toLowerCase().startsWith(q) ||
        addr.Zip_Code.toString().startsWith(q)
      );
    }).slice(0, MAX_RESULTS);

    setSuggestions(filtered);
  };

  const handleAddLocationDirectly = (location) => {
    const trimmedValue = location.trim();
    if (!trimmedValue) {
      setError("Please select a valid Suburb.");
      return;
    }
    if (addedLocations.includes(trimmedValue)) {
      setError("Location already added.");
      return;
    }
    setAddedLocations((prevLocations) => [trimmedValue, ...prevLocations]);
    setSuburbValue("");
    setSuggestions([]);
    setError("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleRemoveLocation = (indexToRemove) => {
    setAddedLocations((prevLocations) =>
      prevLocations.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleModifyPrimaryLocation = () => {
    setPrimaryLocation("");
    navigate("/account/ps-dashboard/account-settings/address");
  };

  const handleSaveLocations = async () => {
    try {
      const updateUserResponse = await axios.patch(
        `${baseURL}/v1/worker-users/${user.id}`,
        {
          locations: addedLocations,
        }
      );

      if (updateUserResponse.data) {
        console.log("Locations update successful");
        setShowModal(true);
      } else {
        console.log("Locations update succeeded but no data returned");
      }
    } catch (error) {
      console.error("Failed to update locations:", error);
    }
  };

  return (
    <div className="locations-profile-custom-component-container">
      {showModal && <SuccessModal />}
      <div className="location-profile-indicative-above-div1">
        <div className="profile-back-container">
          {isSmallScreen && (
            <div className="profile-back-arrow" onClick={handleBackClick}>
              <FaAngleLeft className="profile-back-icon" />
            </div>
          )}
          <h2>Your Primary Location</h2>
        </div>
        <div className="location-profile-added-location">
          {primaryLocation}
          <button
            className="location-profile-modify-button"
            onClick={handleModifyPrimaryLocation}
          >
            Update
          </button>
        </div>

        <p>
          Want to expand your reach? Add service locations where you provide
          support. Simply type an address in the text box below, select from
          suggestions, and it'll be added to your service locations list.
          Remember to save your newly added service location(s).
        </p>
        <h5>Search for service location</h5>
      </div>
      {error && <div className="location-profile-error">{error}</div>}
      <div className="location-profile-input-wrapper">
        <FaSearch className="location-profile-search-icon" />
        <input
          type="text"
          autocomplete="off"
          id="suburb"
          required
          value={suburbValue}
          onChange={handleSuburbChange}
          ref={inputRef}
        />
      </div>

      <div className="search-location-suggestions">
        <ul>
          {suggestions.map((suburb, index) => (
            <li
              key={index}
              onClick={() =>
                handleAddLocationDirectly(
                  `${suburb.Locality} ${suburb.State} ${suburb.Postcode}`
                )
              }
            >
              {suburb.Locality} {suburb.State} {suburb.Postcode}
            </li>
          ))}
        </ul>
      </div>

      {suggestions.length === 0 && (
        <div className="location-profile-added-locations">
          <h3>Selected Service Location(s)</h3>
          {addedLocations
            .filter((location) => location !== primaryLocation)
            .map((location, index) => (
              <div className="location-profile-added-location" key={index}>
                {location}
                <button
                  className="location-profile-remove-location"
                  onClick={() => handleRemoveLocation(index)}
                >
                  Remove
                </button>
              </div>
            ))}
        </div>
      )}

      <div className="location-profile-save">
        <button onClick={handleSaveLocations}>Save and continue</button>
      </div>
    </div>
  );
};

export default LocationsComponent;
